import { ChangeDetectorRef, Directive, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { NgInject } from '@df/ng/ng-inject';
import { CreditCard } from '@df/session/api/credit-cards/credit-card';
import find from 'lodash-es/find';
import first from 'lodash-es/first';
import includes from 'lodash-es/includes';
import isFunction from 'lodash-es/isFunction';
import { Subscription } from 'rxjs';
import { NCheckout } from './df-checkout.config';

export const CHECKOUT_HIDDEN_PAYMENT_METHODS: string[] = [];

@Directive()
export abstract class CheckoutPaymentMethodsComponentDef implements OnInit, OnDestroy, Checkout.ICheckoutPaymentMethodsComponent {
    methods: Checkout.IPaymentMethod[] = [];

    @NgInject(App)
    readonly app: App;
    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;
    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    /**
     * If set then cards methods will be skipped
     */
    @Input()
    noCards: boolean = false;

    /**
     * Override for standard payment data object
     */
    protected _data?: Checkout.IQuoteDataPayment;

    /**
     * Use data input to override standard payment slot
     */
    @Input()
    set data(data: Checkout.IQuoteDataPayment) {
        this._data = data;
        this.onCheckoutChange({ full: true });
    }

    /**
     * Payment method data shorthand
     */
    get data(): Checkout.IQuoteDataPayment {
        return this._data || this.checkoutFormComponent?.data?.payment || {};
    }

    /**
     * Methods list
     */
    protected _methods?: Checkout.IPaymentMethod[];

    protected subscriptions = new Subscription();

    private lastMethods: Checkout.IPaymentMethod[] = [];

    private moduleConfig = inject(NCheckout.OPTIONS);

    /**
     * Class constructor
     */
    constructor(
        protected checkoutFormComponent: CheckoutFormComponentDef,
        protected checkoutStepComponent: CheckoutStepComponentDef,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    /////////////////////////////////////////////
    //
    // EVENTS
    //
    /////////////////////////////////////////////

    /**
     * Set up component
     */
    ngOnInit() {
        this.subscriptions.add(this.checkoutFormComponent.subscribe((change: Checkout.IStateChange) => this.onCheckoutChange(change)));
        this.subscriptions.add(this.app.customer.model.obs.status.subscribe(() => this.onCheckoutChange({ full: true })));
        [this.status, this.saveStatus, this.checkoutFormComponent.saveBillingAddressStatus].forEach(status =>
            this.subscriptions.add(status.subscribe(() => this.detectChanges()))
        );
        this.onCheckoutChange({ full: true });

        this.subscriptions.add(
            this.app.events.on(Checkout.EVENTS.RefreshPaymentMethods).subscribe(() => {
                this.onCheckoutChange({ full: false });
            })
        );
    }

    /**
     * Cleanup
     */
    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        this.checkoutStepComponent.removeForm(this._stepForm);
    }

    /**
     * copies customer payment ethods to local variable
     */
    protected onCheckoutChange(change: Checkout.IStateChange) {
        const methods = [...this.getMethods()];
        this.app.events.broadcast(Checkout.EVENTS.SetPaymentMethodsBefore, methods);
        this.methods = methods;
        if (this.noPaymentNeeded) {
            this.data.method = undefined;
        } else if (this.data.method) {
            // if method is no longer allowed then unset it
            let publicHash = this.data.additional_data?.public_hash;
            const methods = [...this.methods].filter(method => method instanceof CreditCard);

            if (!publicHash && methods.find((method: CreditCard) => method.data.public_hash === this.data.method)) {
                publicHash = this.data.method;
            }

            let cardId: number | undefined = this.data.additional_data?.cardId;

            if (!cardId && methods.find((method: CreditCard) => method.data.id === this.data.method)) {
                cardId = this.data.method as number;
            }

            if (publicHash || cardId) {
                const selectedCard = find(this.checkoutFormComponent.paymentTokens, card => {
                    if (publicHash) {
                        return card.data?.public_hash === publicHash;
                    } else {
                        return card.data?.id === cardId;
                    }
                });
                if (selectedCard) {
                    this.data.method = selectedCard.code;
                } else {
                    this.data.method = undefined;
                }

                delete this.data.additional_data;
            }
            this.validateSelectedMethod();
        }
        this.pickFirst();
        this.detectChanges();
    }

    protected validateSelectedMethod(): void {
        if (!this.methods.find(i => i.code === this.data.method)) {
            this.data.method = undefined;
        }
    }

    /////////////////////////////////////////////
    //
    // COMPONENT DATA
    //
    /////////////////////////////////////////////

    /**
     * Update status shorthand
     */
    get status(): Status {
        return this.checkoutFormComponent.status;
    }

    /**
     * Returns true if Payment method is selection not needed
     */
    get noPaymentNeeded(): boolean {
        return this.checkoutFormComponent.isFree;
    }

    /**
     * Methods getter
     */
    protected getMethods(): Checkout.IPaymentMethod[] {
        let methods: Checkout.IPaymentMethod[] = [];
        if (this.noCards) {
            methods = this.checkoutFormComponent.paymentMethods;
        } else {
            methods = this.checkoutFormComponent.paymentMethodsAndTokens;
            const nonCardMethodCodes = methods.filter(i => !(<Checkout.ICardPaymentMethod>i).isCard).map(i => i.code);
            methods = methods.filter(method => {
                if ((<Checkout.ICardPaymentMethod>method).isCard) {
                    const card = method as CreditCard;
                    const paymentMethodCode = card.data?.method === 'vault' ? card.data?.payment_method : card.data?.method;
                    return nonCardMethodCodes.includes(paymentMethodCode);
                }
                return true;
            });
        }

        if (!CHECKOUT_HIDDEN_PAYMENT_METHODS.length) {
            return methods;
        }

        const newMethods = methods.filter(
            method => typeof method.code !== 'string' || !includes(CHECKOUT_HIDDEN_PAYMENT_METHODS, method.code)
        );

        if (newMethods.length !== this.lastMethods.length || find(newMethods, (method, index) => method !== this.lastMethods[index])) {
            this.lastMethods = newMethods;
        }
        return this.lastMethods;
    }

    /**
     * Retrieves method instance
     */
    getMethod(code: string | number | undefined = this.data.method): Checkout.IPaymentMethod | undefined {
        if (!code) {
            return undefined;
        }
        if (this._methods) {
            return this._methods.find(i => i.code === code);
        }
        return this.checkoutFormComponent.paymentMethodByCode(code);
    }

    /**
     * Picks first non pickup method if checkoutConfig allows it
     */
    protected pickFirst() {
        if (this.data) {
            let methods: Checkout.IPaymentMethod[] | undefined;
            if (!this.data.method) {
                // autoPickCheepestPaymentMethod
                if (this.moduleConfig.autoPickSavedCard) {
                    const savedCards = this.checkoutFormComponent.paymentTokens;
                    const card = savedCards.find(savedCard => savedCard.data.is_default) || first(savedCards);
                    if (card) {
                        this.data.method = card.code;
                    }
                }
            }

            // only proceed if still not selected
            if (!this.data.method) {
                // autoPickPaymentMethodIfOnlyOneAvailable
                if (!methods && this.moduleConfig.autoPickPaymentMethodIfOnlyOneAvailable && this.methods.length === 1) {
                    this.data.method = this.methods[0].code;
                    this.onMethodPick();
                } else {
                    // autoPickPaymentMethod
                    const autoPick = this.moduleConfig.autoPickPaymentMethod;
                    if (autoPick) {
                        if (isFunction(autoPick)) {
                            methods = autoPick(this);
                        } else if (autoPick === true) {
                            methods = this.methods;
                        } else if (Array.isArray(autoPick)) {
                            methods = this.methods.filter(method => autoPick.includes(<string>method.code));
                        }
                    }

                    if (methods && methods.length) {
                        this.data.method = methods[0].code;
                        this.onMethodPick();
                    }
                    // make sure that method is undefined (otherwise our watch wont work as it skips change from undefiend to some value)
                    if (!this.data.method) {
                        this.data.method = undefined;
                    }
                }
            }
        }
    }

    protected onMethodPick(): void {
        const paymentMethod = this.checkoutFormComponent.paymentCardByCode(this.data.method);
        const eventData: Checkout.ISavePaymentMethodEventData = { ...this.data, ver: 2, methodDetails: paymentMethod };

        this.app.events.broadcast<Checkout.ISavePaymentMethodEventData>(Checkout.EVENTS.PickPaymentMethod, eventData);
    }

    get saveStatus(): Status {
        return this.checkoutFormComponent.savePaymentMethodStatus;
    }

    protected detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    methodTrackFn(index, method: Checkout.IPaymentMethod) {
        return method.code;
    }

    isSelected(method: Checkout.IPaymentMethod): boolean {
        return this.checkoutFormComponent.paymentMethodCode === method.code;
    }
}
